import React from 'react';
import { BaseTag } from '@components';
import { DATE_FORMATS } from '@constants/date.ts';
import { MULTI_SELECT_TABLE_VIEW_VALUE_TAG_CLASS } from '@constants/table.ts';
import {
    EDITABLE_FIELDS,
    NOT_SET_LABEL,
} from '@routes/sidebar/admin-center/___shared___/components/tables/constants.ts';
import { editorSwitch, showNoSetValue } from '@routes/sidebar/admin-center/___shared___/components/tables/helpers.tsx';
import type { SelectOption } from '@routes/sidebar/admin-center/___shared___/components/tables/types.ts';
import type { ReferencesColumn } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/types.ts';
import { formatDateByPreferredFormat } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/utils.tsx';
import type { MatricesCustomSelectData } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/table/utils.ts';
import { translateItem } from '@utils/translations.ts';
import type { FormInstance } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { isNil } from 'lodash';

type ToggleHandlers = {
    onToggleChange?: (checked: boolean) => void;
    disabled?: boolean;
    namePath?: (string | number)[];
    initialValue?: boolean;
    renderInView?: boolean;
};

type SingleSelectHandlers = {
    value?: string | null;
    options?: SelectOption[];
    onChange?: (value: string) => void;
    disabled?: boolean;
    placeholder?: string;
    allowSingleSearch?: boolean;
};

type BuildColumnsOpts<R extends { key: React.Key }> = Parameters<typeof editorSwitch>[0]['opts'] & {
    draftKey?: React.Key;
    creationColumnsByDataIndex?: Record<string, ReferencesColumn>;
    getEditorSearchHandlers?: (col: ReferencesColumn) => {
        allowSingleSearch?: boolean;
        onSingleSearch?: (value: string) => void;
        allowMultiSearch?: boolean;
        onMultiSearch?: (value: string) => void;
    };
    getEditorToggleHandlers?: (col: ReferencesColumn, record: R) => ToggleHandlers;
    getEditorSingleSelectHandlers?: (col: ReferencesColumn, record: R) => SingleSelectHandlers;
    getEditorFieldProps?: (col: ReferencesColumn, record: R) => { disabled?: boolean };
    customRankAndVesselSelectData?: { ranksData?: MatricesCustomSelectData; vesselsData?: MatricesCustomSelectData };
    placeholders?: { select?: string; dataPicker?: string; input?: string };
};

export const buildEditOrViewColumns = <R extends { key: React.Key }>(
    cols: ReferencesColumn[],
    isEditing: (row: R) => boolean,
    opts?: BuildColumnsOpts<R>,
    form?: FormInstance<any>,
): ColumnsType<R> =>
    cols.map((col) => ({
        ...col,
        key: col.key,
        title: translateItem(col.title),
        dataIndex: col.dataIndex,
        width: col.width,
        render: (value: any, record: R) => {
            const isDraft = !isNil(opts?.draftKey) && record.key === opts.draftKey;
            const creationOverride = isDraft ? opts?.creationColumnsByDataIndex?.[col.dataIndex] : undefined;
            const effCol = creationOverride ? { ...col, ...creationOverride } : col;

            const searchHandlers = opts?.getEditorSearchHandlers?.(effCol) ?? {};

            const toggleHandlers: ToggleHandlers =
                effCol.type === EDITABLE_FIELDS.TOGGLE ? (opts?.getEditorToggleHandlers?.(effCol, record) ?? {}) : {};

            const getOptionsForCell = (dataSource: string): SelectOption[] => {
                if (opts?.getOptions) return opts.getOptions(dataSource, record);
                return [];
            };

            const cellOpts = opts
                ? {
                      ...opts,
                      getOptions: getOptionsForCell,
                  }
                : undefined;

            const showEditableToggleInViewMode =
                effCol.editable && effCol.type === EDITABLE_FIELDS.TOGGLE && toggleHandlers.renderInView;

            // Special-case only if renderInView was provided for this column
            if (showEditableToggleInViewMode) {
                const rowKey = String(record.key);

                return editorSwitch({
                    col: {
                        ...effCol,
                        disabled: toggleHandlers.disabled ?? false,
                        namePath: toggleHandlers.namePath ?? ['rows', rowKey, effCol.dataIndex],
                        initialValue:
                            toggleHandlers.initialValue ?? Boolean(value ?? (record as any)?.[effCol.dataIndex]),
                        onToggleChange: toggleHandlers.onToggleChange,
                    },
                    opts: cellOpts,
                });
            }

            // VIEW MODE → for custom single select (assessors)
            if (effCol.type === EDITABLE_FIELDS.CUSTOM_SINGLE_SELECT) {
                const singleSelectHandlers = opts?.getEditorSingleSelectHandlers?.(effCol, record) ?? {};

                return editorSwitch({
                    col: {
                        ...effCol,
                        editable: true,
                        value: singleSelectHandlers.value,
                        disabled: singleSelectHandlers.disabled,
                        placeholder: singleSelectHandlers.placeholder,
                    },
                    opts: {
                        ...cellOpts,
                        getOptions: singleSelectHandlers.options
                            ? () => singleSelectHandlers.options ?? []
                            : getOptionsForCell,
                    },
                    allowSingleSearch: singleSelectHandlers.allowSingleSearch ?? false,
                    onSingleChange: singleSelectHandlers.onChange,
                    record,
                });
            }

            const fieldPropsWIthRecord = opts?.getEditorFieldProps?.(effCol, record) ?? {};

            // normal editable fields only in edit/draft (unchanged behavior)
            if (effCol.editable && (isEditing(record) || isDraft)) {
                return editorSwitch({
                    col: { ...effCol, ...fieldPropsWIthRecord },
                    opts: cellOpts,
                    allowSingleSearch: searchHandlers.allowSingleSearch,
                    onSingleSearch: searchHandlers.onSingleSearch,
                    allowMultiSearch: searchHandlers.allowMultiSearch,
                    onMultiSearch: searchHandlers.onMultiSearch,
                    form,
                    record,
                });
            }

            // VIEW MODE → format dates if this is a DATE_PICKER column
            if (effCol.type === EDITABLE_FIELDS.DATE_PICKER) {
                const formatted = formatDateByPreferredFormat(value, opts?.dateFormate ?? DATE_FORMATS.ISO);

                return formatted ?? showNoSetValue(value);
            }

            if (
                effCol.type === EDITABLE_FIELDS.SINGLE_SELECT ||
                effCol.type === EDITABLE_FIELDS.MULTI_SELECT ||
                effCol.type === EDITABLE_FIELDS.CUSTOM_SINGLE_SELECT_MATRICES ||
                effCol.type === EDITABLE_FIELDS.CUSTOM_COMMON_MULTI_SELECT ||
                effCol.type === EDITABLE_FIELDS.CUSTOM_MULTI_SELECT_MATRICES_VESSELS ||
                effCol.type === EDITABLE_FIELDS.CUSTOM_MULTI_SELECT_MATRICES_RANKS
            ) {
                const options: SelectOption[] = effCol.dataSource ? getOptionsForCell(effCol.dataSource) : [];

                if (
                    effCol.type === EDITABLE_FIELDS.SINGLE_SELECT ||
                    effCol.type === EDITABLE_FIELDS.CUSTOM_SINGLE_SELECT_MATRICES
                ) {
                    const label = options.find((o) => String(o.value) === String(value))?.label;
                    return label ?? NOT_SET_LABEL();
                }

                if (Array.isArray(value) && value.length) {
                    const labels = value
                        .map((v) => options.find((o) => o.value === v)?.label)
                        .filter(Boolean) as string[];
                    return labels.length
                        ? labels.map((label) => (
                              <BaseTag className={MULTI_SELECT_TABLE_VIEW_VALUE_TAG_CLASS} key={label}>
                                  {label}
                              </BaseTag>
                          ))
                        : NOT_SET_LABEL();
                }
                return NOT_SET_LABEL();
            }

            return showNoSetValue(value);
        },
    }));
